/**
 * Per-environment kill switches for the third-party / AI surfaces: ELSA, Atlas
 * and Zendesk.
 *
 * Two layers decide whether one of these is drawn, and BOTH must say yes:
 *
 *   1. the backend's per-user `features` block (`authuser`) — who may use it, and
 *   2. the switches here — whether this BUILD may use it at all.
 *
 * The second layer exists because the first cannot tell environments apart: a
 * staging backend that grants `create_with_atlas` to a user makes the drawer
 * appear, and every turn of it is a paid model call. Zendesk is the same shape —
 * a dev build must never file tickets into the real queue. With no switch of
 * their own, working on these surfaces meant testing against production.
 *
 * Resolution, per feature:
 *   - `VITE_FEATURE_<NAME>` = 'true' | 'false' wins when it is set;
 *   - otherwise ON only when `VITE_APP_NODE_ENV` is 'production'.
 *
 * The default is deliberately closed. A new environment, a missing line or a
 * typo in an env file leaves the feature OFF; production has to opt in, and its
 * env file does so explicitly. To work on a feature locally, put the switch in
 * an untracked `.env.development.local` rather than editing the tracked files.
 *
 * This hides the UI. It is not a security boundary: the backend must refuse the
 * same calls on its own (the Zendesk and agent routes already 403 by role).
 */

export type EnvFeature = 'elsa' | 'atlas' | 'zendesk';

/** Read per call, not at import, so tests can stub the env. */
const raw = (feature: EnvFeature): string | undefined => {
  switch (feature) {
    case 'elsa':
      return import.meta.env.VITE_FEATURE_ELSA;
    case 'atlas':
      return import.meta.env.VITE_FEATURE_ATLAS;
    case 'zendesk':
      return import.meta.env.VITE_FEATURE_ZENDESK;
    default:
      return undefined;
  }
};

export const envFeatureEnabled = (feature: EnvFeature): boolean => {
  const value = String(raw(feature) ?? '')
    .trim()
    .toLowerCase();
  if (value === 'true') return true;
  if (value === 'false') return false;
  return import.meta.env.VITE_APP_NODE_ENV === 'production';
};

/** Backend `features` keys that belong to each switch. */
const ELSA_KEYS = ['ask_elsa', 'create_order_with_elsa'] as const;

const ATLAS_KEYS = [
  'create_with_atlas',
  'create_role_with_atlas',
  'create_permission_with_atlas',
  'create_design_category_with_atlas',
  'create_kickback_with_atlas',
  'create_company_with_atlas',
  'create_development_with_atlas',
  'draft_quote_follow_up_with_atlas',
  'create_price_list_with_atlas',
] as const;

const ZENDESK_KEYS = ['order_communication_all_merchants'] as const;

/**
 * The backend's `features` with every flag of a switched-off feature forced to
 * false. Applied once, where the block enters the store, so the many call sites
 * that read `store.features` need no change. Flags the backend did not send stay
 * absent: a missing key is not a `false`, and some readers fall back on it.
 */
export const maskFeatures = <T extends object | undefined>(features: T): T => {
  if (!features) return features;

  const off: string[] = [];
  if (!envFeatureEnabled('elsa')) off.push(...ELSA_KEYS);
  if (!envFeatureEnabled('atlas')) off.push(...ATLAS_KEYS);
  if (!envFeatureEnabled('zendesk')) off.push(...ZENDESK_KEYS);
  if (off.length === 0) return features;

  const masked = { ...features } as Record<string, unknown>;
  for (const key of off) {
    if (key in masked) masked[key] = false;
  }
  return masked as T;
};
